/**
 * Reading and presenting a generator's location pin (`_opgUnit.pin` on an
 * `appsheet_generators` doc).
 *
 * Nick, 2026-09-27: a pin comes ONLY from a person placing it or from photo
 * GPS. The street address is never a stand-in for the pin, so nothing here
 * turns an address into coordinates; the address has its own, separately
 * named directions link for units that have no pin yet.
 *
 * Plan: docs/superpowers/plans/2026-09-26-generator-location-pinning.md, Task 4.1.
 */
import { haversineMeters } from '@officehub/shared';
import type {
  GeneratorPinDto,
  GeneratorPinReason,
  GeneratorPinSource,
} from '@officehub/shared';
import type { StatusTone } from '../../components/shared/statusTone';

export interface GeneratorPinSetByView {
  uid: string;
  displayName: string;
}

export interface GeneratorPinHistoryView {
  /** Null only if a stored entry has no usable point. Every replacement reason keeps the old point. */
  lat: number | null;
  lng: number | null;
  source: GeneratorPinSource;
  setBy: GeneratorPinSetByView | null;
  setAt: Date | null;
  replacedReason: string | null;
}

export interface GeneratorPinView {
  lat: number;
  lng: number;
  /** Metres. Null for a map-placed pin. */
  accuracy: number | null;
  source: GeneratorPinSource;
  setBy: GeneratorPinSetByView | null;
  setAt: Date | null;
  confirmedAt: Date | null;
  /** Photo-derived pins only: how many photos agreed. */
  photoCount: number | null;
  /** Newest first. */
  history: GeneratorPinHistoryView[];
}

const PIN_SOURCES: readonly GeneratorPinSource[] = ['tech-pin', 'office', 'photo-exif', 'photo-derived'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSource(value: unknown): value is GeneratorPinSource {
  return typeof value === 'string' && (PIN_SOURCES as readonly string[]).includes(value);
}

function finiteInRange(value: unknown, limit: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= limit;
}

/** Firestore Timestamp, a serialized `{_seconds}`/`{seconds}`, a Date or an ISO string. */
function toDate(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (isRecord(value)) {
    if (typeof value.toDate === 'function') return toDate((value.toDate as () => unknown)());
    const seconds = value._seconds ?? value.seconds;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) return new Date(seconds * 1000);
  }
  return null;
}

function readSetBy(value: unknown): GeneratorPinSetByView | null {
  if (!isRecord(value) || typeof value.uid !== 'string') return null;
  return { uid: value.uid, displayName: typeof value.displayName === 'string' ? value.displayName : '' };
}

function readHistoryEntry(value: unknown): GeneratorPinHistoryView | null {
  if (!isRecord(value) || !isSource(value.source)) return null;
  const hasPoint = finiteInRange(value.lat, 90) && finiteInRange(value.lng, 180);
  return {
    lat: hasPoint ? (value.lat as number) : null,
    lng: hasPoint ? (value.lng as number) : null,
    source: value.source,
    setBy: readSetBy(value.setBy),
    setAt: toDate(value.setAt),
    replacedReason: typeof value.replacedReason === 'string' ? value.replacedReason : null,
  };
}

/** Parse a raw pin object (doc field or callable DTO). Null unless it is a usable point. */
function readPin(raw: unknown): GeneratorPinView | null {
  if (!isRecord(raw)) return null;
  if (!finiteInRange(raw.lat, 90) || !finiteInRange(raw.lng, 180) || !isSource(raw.source)) return null;
  const evidence = isRecord(raw.evidence) ? raw.evidence : null;
  const history = Array.isArray(raw.history)
    ? raw.history.map(readHistoryEntry).filter((h): h is GeneratorPinHistoryView => h !== null)
    : [];
  return {
    lat: raw.lat,
    lng: raw.lng,
    accuracy: typeof raw.accuracy === 'number' && Number.isFinite(raw.accuracy) ? raw.accuracy : null,
    source: raw.source,
    setBy: readSetBy(raw.setBy),
    setAt: toDate(raw.setAt),
    confirmedAt: toDate(raw.confirmedAt),
    photoCount: evidence && typeof evidence.photoCount === 'number' ? evidence.photoCount : null,
    // Stored oldest-first (the server appends); shown newest-first.
    history: [...history].reverse(),
  };
}

/**
 * The pin on a generator doc, or null. Tolerates a missing or partial
 * `_opgUnit` (most docs have none), and any malformed pin.
 */
export function readGeneratorPin(doc: unknown): GeneratorPinView | null {
  if (!isRecord(doc) || !isRecord(doc._opgUnit)) return null;
  return readPin(doc._opgUnit.pin);
}

/** The pin the callable returned (ISO-string timestamps) in the same view shape. */
export function pinFromDto(dto: GeneratorPinDto): GeneratorPinView | null {
  return readPin(dto);
}

const DIRECTIONS_BASE = 'https://www.google.com/maps/dir/?api=1&destination=';

/**
 * Turn-by-turn directions to the PIN. The universal Maps URL opens the native
 * Google Maps app on iOS and Android (web otherwise). Never takes an address.
 */
export function buildNavigateUrl({ lat, lng }: { lat: number; lng: number }): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error('buildNavigateUrl: the pin has no usable coordinates');
  }
  return `${DIRECTIONS_BASE}${lat},${lng}`;
}

/** Directions to the site ADDRESS: only for a unit with no pin. Null when blank. */
export function buildAddressDirectionsUrl(address: string | null | undefined): string | null {
  const trimmed = (address ?? '').trim();
  return trimmed ? `${DIRECTIONS_BASE}${encodeURIComponent(trimmed)}` : null;
}

/** The job-screen button labels the visit prompt quotes: one source, so a rename cannot make the prompt lie. */
export const PIN_HERE_LABEL = 'Pin this location';
export const CONFIRM_HERE_LABEL = "Yes, it's here";

/**
 * Does this unit need a person standing at the machine to settle its location? (GPS pin lane, Nick
 * 2026-10-07: tech taps on site are the only way a position gets PROVED.) A flag for the job screen,
 * never a gate: it must not stop a job, an FSR or anything else.
 *  - 'no-pin': nothing is pinned yet.
 *  - 'unchecked': a pin exists that nobody stood beside (office map pin, accepted or derived photo pin).
 *  - null: an on-site pin, or a pin already confirmed on site. (A pin with confirmedAt set still offers
 *    "Yes, it's here" but is not nagged: someone already stood beside it.)
 */
export type VisitPinPrompt = 'no-pin' | 'unchecked' | null;

export function visitPinPrompt(pin: Pick<GeneratorPinView, 'source' | 'confirmedAt'> | null): VisitPinPrompt {
  if (!pin) return 'no-pin';
  if (pin.source === 'tech-pin' || pin.confirmedAt != null) return null;
  return 'unchecked';
}

/** The short line shown for each prompt state; it names the real button labels. */
export const VISIT_PIN_PROMPT_TEXT: Record<Exclude<VisitPinPrompt, null>, string> = {
  'no-pin': `No confirmed location yet. At the machine? Tap "${PIN_HERE_LABEL}".`,
  unchecked: `Pin not checked at the machine. At it? Tap "${CONFIRM_HERE_LABEL}".`,
};

/** One plain-English line saying where the pin came from. */
export function pinLabel(pin: Pick<GeneratorPinView, 'source' | 'photoCount'>): string {
  switch (pin.source) {
    case 'tech-pin':
      return 'Confirmed on site';
    case 'office':
      return 'Set by office';
    case 'photo-exif':
      return 'From photo location';
    case 'photo-derived': {
      const n = pin.photoCount;
      const photos = n == null ? 'past visit photos' : `${n} past visit ${n === 1 ? 'photo' : 'photos'}`;
      return `From ${photos} · not yet confirmed`;
    }
  }
}

/** Badge tone: a person on site is settled; a photo-derived guess needs a person. */
export function pinTone(source: GeneratorPinSource): StatusTone {
  switch (source) {
    case 'tech-pin':
      return 'done';
    case 'office':
    case 'photo-exif':
      return 'active';
    case 'photo-derived':
      return 'attention';
  }
}

const REASON_LABELS: Record<GeneratorPinReason, string> = {
  'wrong-location': 'Old pin was in the wrong place',
  'unit-moved': 'The unit was moved',
  'more-precise': 'A more precise spot',
};

export function pinReasonLabel(reason: GeneratorPinReason): string {
  return REASON_LABELS[reason];
}

/** History reasons the SERVER writes (a person does not choose these). */
const SERVER_REASON_LABELS: Record<string, string> = {
  'confirmed-on-site': 'Confirmed on site by a tech',
  'suggestion-accepted': 'Replaced by an accepted photo suggestion',
};

/** Plain English for any history reason; an unknown one is shown as stored. */
export function historyReasonLabel(reason: string | null): string | null {
  if (!reason) return null;
  if ((Object.keys(REASON_LABELS) as string[]).includes(reason)) return REASON_LABELS[reason as GeneratorPinReason];
  return SERVER_REASON_LABELS[reason] ?? reason;
}

/** Sources a tech standing at the unit can confirm ("Yes, it's here"). A tech-pin is already confirmed. */
export function canConfirmPin(pin: Pick<GeneratorPinView, 'source'> | null): boolean {
  return pin !== null && pin.source !== 'tech-pin';
}

export interface PinSuggestionView {
  id: string;
  /** Null when the point was withheld: shown as a warning, never acceptable. */
  lat: number | null;
  lng: number | null;
  photoCount: number;
  visitCount: number;
  spreadMeters: number;
  distanceFromAddressMeters: number | null;
  gateFailures: string[];
  suggestedAt: Date | null;
  /** Local days the photos were taken (YYYY-MM-DD, sorted); [] when the suggestion predates them. */
  visitDays: string[];
  /** Each trip's photo day beside its report's day (sorted by photo day); [] when the suggestion predates them. */
  visitReports: VisitReportView[];
}

/** One trip: when its photos were taken, and when its report was filed (null when unknown). */
export interface VisitReportView {
  photoDay: string;
  reportDay: string | null;
}

const LOCAL_DAY = /^\d{4}-\d{2}-\d{2}$/;

function readVisitReports(raw: unknown): VisitReportView[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((e): VisitReportView[] => {
    if (!isRecord(e) || typeof e.photoDay !== 'string' || !LOCAL_DAY.test(e.photoDay)) return [];
    const reportDay = typeof e.reportDay === 'string' && LOCAL_DAY.test(e.reportDay) ? e.reportDay : null;
    return [{ photoDay: e.photoDay, reportDay }];
  });
}

function readSuggestion(raw: unknown): PinSuggestionView | null {
  if (!isRecord(raw) || typeof raw.id !== 'string' || raw.status !== 'pending') return null;
  const hasPoint = finiteInRange(raw.lat, 90) && finiteInRange(raw.lng, 180);
  const evidence = isRecord(raw.evidence) ? raw.evidence : {};
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return {
    id: raw.id,
    lat: hasPoint ? (raw.lat as number) : null,
    lng: hasPoint ? (raw.lng as number) : null,
    photoCount: num(evidence.photoCount),
    visitCount: num(evidence.visitCount),
    spreadMeters: num(evidence.spreadMeters),
    distanceFromAddressMeters:
      typeof evidence.distanceFromAddressMeters === 'number' ? evidence.distanceFromAddressMeters : null,
    gateFailures: Array.isArray(raw.gateFailures) ? raw.gateFailures.filter((g): g is string => typeof g === 'string') : [],
    suggestedAt: toDate(raw.suggestedAt),
    visitDays: Array.isArray(evidence.visitDays)
      ? evidence.visitDays.filter((d): d is string => typeof d === 'string' && LOCAL_DAY.test(d))
      : [],
    visitReports: readVisitReports(evidence.visitReports),
  };
}

const MAX_DAYS_SHOWN = 6;

/**
 * When a suggestion's photos were taken, for the reviewer: the newest six days, then "+N earlier".
 * An old photo, or a picture of a picture, shows up as a date far from the others.
 */
export function visitDaysLabel(days: readonly string[]): string | null {
  if (days.length === 0) return null;
  const shown = days.slice(-MAX_DAYS_SHOWN);
  const earlier = days.length - shown.length;
  return `Photos taken: ${shown.join(', ')}${earlier > 0 ? ` (+${earlier} earlier)` : ''}`;
}

/** A photo day more than this many days from its report's day is flagged to the reviewer. */
export const REPORT_GAP_WARN_DAYS = 7;

const DAY_MS = 86_400_000;

/** Whole days between two local YYYY-MM-DD days (both read as UTC midnight, so no zone shift). */
function dayGap(a: string, b: string): number {
  return Math.round(Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / DAY_MS);
}

/**
 * When a suggestion's photos were taken, each beside its report's day when that differs: the newest
 * six trips, then "+N earlier". Null when there are none (the caller falls back to visitDaysLabel).
 */
export function visitReportsLabel(reports: readonly VisitReportView[]): string | null {
  if (reports.length === 0) return null;
  const shown = reports.slice(-MAX_DAYS_SHOWN);
  const earlier = reports.length - shown.length;
  const part = (r: VisitReportView) =>
    r.reportDay === null
      ? `${r.photoDay} (report date unknown)`
      : r.reportDay === r.photoDay
        ? r.photoDay
        : `${r.photoDay} (report ${r.reportDay})`;
  return `Photos taken: ${shown.map(part).join(', ')}${earlier > 0 ? ` (+${earlier} earlier)` : ''}`;
}

/**
 * A warning when any photo date is more than REPORT_GAP_WARN_DAYS from its report: an old photo, or a
 * picture of a picture, carries where and when the re-shoot happened, not the visit. Counts the
 * (photo date, report date) entries it is given, which is what the card lists; says so when a flagged
 * entry is older than the ones visitReportsLabel shows.
 */
export function reportGapWarning(reports: readonly VisitReportView[]): string | null {
  const shownFrom = Math.max(0, reports.length - MAX_DAYS_SHOWN);
  const flagged = reports.flatMap((r, i) => {
    if (r.reportDay === null) return [];
    const gap = dayGap(r.photoDay, r.reportDay);
    return gap > REPORT_GAP_WARN_DAYS ? [{ gap, notListed: i < shownFrom }] : [];
  });
  if (flagged.length === 0) return null;
  const dates = reports.length === 1 ? 'photo date' : 'photo dates';
  const verb = flagged.length === 1 ? 'is' : 'are';
  const earlier = flagged.some((f) => f.notListed) ? ', including earlier dates not listed' : '';
  return (
    `${flagged.length} of ${reports.length} ${dates} ${verb} more than ${REPORT_GAP_WARN_DAYS} days from the report ` +
    `(up to ${Math.max(...flagged.map((f) => f.gap))} days${earlier}). Check for old photos or pictures of pictures.`
  );
}

/**
 * The PENDING suggestions on a generator doc (or a queue item's list), newest first. Decided ones
 * are history, not work, and are not shown.
 */
export function readPinSuggestions(source: unknown): PinSuggestionView[] {
  const list = Array.isArray(source)
    ? source
    : isRecord(source) && isRecord(source._opgUnit) && Array.isArray(source._opgUnit.pinSuggestions)
      ? source._opgUnit.pinSuggestions
      : [];
  return list
    .map(readSuggestion)
    .filter((s): s is PinSuggestionView => s !== null)
    .reverse();
}

/** One line on why a suggestion needs a person rather than becoming a pin on its own. */
export function suggestionSummary(s: PinSuggestionView): string {
  const photos = `${s.photoCount} ${s.photoCount === 1 ? 'photo' : 'photos'}`;
  const visits = `${s.visitCount} ${s.visitCount === 1 ? 'visit' : 'visits'}`;
  return `From ${photos} over ${visits}, spread ±${Math.round(s.spreadMeters)} m`;
}

/** Why a photo suggestion needed a person, in plain English. Unknown codes are shown as stored. */
const GATE_FAILURE_LABELS: Record<string, string> = {
  'min-photos': 'Few photos',
  'min-visits': 'Only one visit',
  'cluster-share': 'The photos disagree about the spot',
  spread: 'The photos are spread out',
  'address-distance': 'Far from the site address',
  'no-address': 'No site address to check against',
  // Legacy: retired in pin-derive-3 (EXIF GPS only), kept so any pin-derive-2 suggestion still reads.
  'meta-only': 'No photo carried its own GPS',
  'conflict-rate': 'Many photos had conflicting GPS',
  'multi-unit-site': 'The site has more than one unit',
  'moved-unit': 'The unit may have been moved',
  'depot-unresolved': 'Branch yards not located',
  'depot-mismatch': 'Checked against old branch yard data',
  'near-depot': 'Near a branch yard (photos may be from the shop)',
};

export function gateFailureLabel(code: string): string {
  return GATE_FAILURE_LABELS[code] ?? code;
}

/** Distance in metres between two points (the shared haversine, which takes lat/lon). */
export function metersBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  return haversineMeters({ lat: a.lat, lon: a.lng }, { lat: b.lat, lon: b.lng });
}

/** Shown wherever a pin action needs a connection. */
export const NEEDS_SIGNAL_MESSAGE = 'Needs signal to save a pin';

/**
 * Why a suggestion with a branch-yard gate cannot be used as the pin, per gate. near-depot is about
 * the photos; the other two are about the yard DATA at the time (a later run can raise it again).
 */
const YARD_REFUSAL_TEXT: Record<string, string> = {
  'near-depot': 'The photos were taken near a branch yard, so they may show the unit at the shop, not at the site.',
  'depot-mismatch': 'This was found using outdated branch yard locations. A later run can suggest it again.',
  'depot-unresolved': 'Branch yard locations were not known when this was found. A later run can suggest it again.',
};

/** The reviewer-facing reason a suggestion cannot be accepted, or null when it can. */
export function acceptRefusalText(gates: readonly string[]): string | null {
  const g = gates.find((x) => x in YARD_REFUSAL_TEXT);
  return g ? YARD_REFUSAL_TEXT[g] : null;
}

// ---------------------------------------------------------------------------------------------
// Map picture of the pin (#12, Nick 2026-09-29): downloaded when the job loads (usually at the
// office, with signal) so it still shows at the site whatever the signal; tapping it with signal
// opens the zoomable map. The URL is built from fixed parameters so the copy the tech app's
// service worker saved is found again byte-for-byte offline.
// ---------------------------------------------------------------------------------------------

const STATIC_MAP_BASE = 'https://maps.googleapis.com/maps/api/staticmap';
/**
 * Fixed so the cached URL always matches: 400x240 at scale 2 (sharp on a phone), satellite with
 * labels, zoom 18, JPEG. Measured 2026-09-29: ~86 KB, against ~524 KB for a 600x300 PNG.
 */
const STATIC_MAP_PARAMS = 'size=400x240&scale=2&format=jpg&maptype=hybrid&zoom=18';

/** The browser Maps key (office: REACT_APP_*; tech app: VITE_* mapped by its envShim). */
export function mapsBrowserKey(): string | null {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  return typeof key === 'string' && key.trim() ? key.trim() : null;
}

/** The map picture of a pin, or null without a key or a usable point. */
export function buildPinStaticMapUrl(pin: { lat: number; lng: number }, apiKey: string | null): string | null {
  if (!apiKey || !Number.isFinite(pin.lat) || !Number.isFinite(pin.lng)) return null;
  const point = `${pin.lat.toFixed(6)},${pin.lng.toFixed(6)}`;
  return `${STATIC_MAP_BASE}?center=${point}&${STATIC_MAP_PARAMS}&markers=${encodeURIComponent(`color:red|${point}`)}&key=${encodeURIComponent(apiKey)}`;
}

/** The zoomable map (Google Maps app / web) centred on the pin, with the phone's own blue dot. */
export function buildPinZoomableMapUrl(pin: { lat: number; lng: number }): string {
  return `https://www.google.com/maps/search/?api=1&query=${pin.lat},${pin.lng}`;
}

/**
 * Download the pin's map picture now, so the tech app's service worker keeps it for offline use.
 * A normal CORS fetch (Static Maps answers `access-control-allow-origin: *`), so the stored
 * response is readable and small, not an opaque one browsers count as ~7 MB of quota.
 * Only when a service worker controls the page: the office app also mounts /technician/*
 * (manager view-as) with no service worker, so a warm-up there would store nothing and just
 * re-request a billable Static Maps picture on every foreground.
 * Never throws: a warm-up failure only means the picture shows online-only.
 */
export async function warmPinMapImage(pin: { lat: number; lng: number } | null | undefined): Promise<void> {
  if (!pin || typeof fetch !== 'function') return;
  if (typeof navigator === 'undefined' || !navigator.serviceWorker?.controller) return;
  const url = buildPinStaticMapUrl(pin, mapsBrowserKey());
  if (!url) return;
  try {
    await fetch(url, { mode: 'cors', credentials: 'omit' });
  } catch {
    // Offline or blocked: the picture falls back to "available with signal".
  }
}
